if (!customElements.get('multicolumn-icon')) {
  customElements.define(
    'multicolumn-icon',
    class MulticolumnIcon extends HTMLElement {
      constructor() {
        super();
        this.autoScrollRaf = null;
        this.resumeTimer = null;
        this.resizeRaf = null;
        this.lastTimestamp = 0;
        this.isInteracting = false;
        this.isVisible = true;
        this.loopWidth = 0;
        this.speed = 0.045;
        this.onModeChange = this.refresh.bind(this);
        this.onInteractionStart = this.pauseForInteraction.bind(this);
        this.onInteractionEnd = this.resumeAfterInteraction.bind(this);
        this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      }

      connectedCallback() {
        this.inner = this.querySelector('.multicolumn-icon__auto-scroll-inner');
        this.mobileQuery = window.matchMedia('(max-width: 767.98px)');
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.bindEvents();
        this.observeVisibility();
        this.observeResize();
        this.refresh();
      }

      disconnectedCallback() {
        this.unbindEvents();
        this.resizeObserver?.disconnect();
        this.intersectionObserver?.disconnect();
        cancelAnimationFrame(this.resizeRaf);
        this.stop();
        this.removeClones();
      }

      get enabledForCurrentMode() {
        if (this.reducedMotionQuery.matches) return false;
        return this.mobileQuery.matches
          ? this.dataset.enableAutoScrollMobile === 'true'
          : this.dataset.enableAutoScrollDesktop === 'true';
      }

      bindEvents() {
        [this.mobileQuery, this.reducedMotionQuery].forEach((query) => {
          if (query.addEventListener) query.addEventListener('change', this.onModeChange);
          else query.addListener(this.onModeChange);
        });

        if (window.PointerEvent) {
          this.addEventListener('pointerdown', this.onInteractionStart, { passive: true });
          window.addEventListener('pointerup', this.onInteractionEnd, { passive: true });
          window.addEventListener('pointercancel', this.onInteractionEnd, { passive: true });
        } else {
          this.addEventListener('touchstart', this.onInteractionStart, { passive: true });
          window.addEventListener('touchend', this.onInteractionEnd, { passive: true });
          window.addEventListener('touchcancel', this.onInteractionEnd, { passive: true });
        }

        this.addEventListener('focusin', this.onInteractionStart);
        this.addEventListener('focusout', this.onInteractionEnd);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
      }

      unbindEvents() {
        [this.mobileQuery, this.reducedMotionQuery].forEach((query) => {
          if (!query) return;
          if (query.removeEventListener) query.removeEventListener('change', this.onModeChange);
          else query.removeListener(this.onModeChange);
        });

        this.removeEventListener('pointerdown', this.onInteractionStart);
        window.removeEventListener('pointerup', this.onInteractionEnd);
        window.removeEventListener('pointercancel', this.onInteractionEnd);
        this.removeEventListener('touchstart', this.onInteractionStart);
        window.removeEventListener('touchend', this.onInteractionEnd);
        window.removeEventListener('touchcancel', this.onInteractionEnd);
        this.removeEventListener('focusin', this.onInteractionStart);
        this.removeEventListener('focusout', this.onInteractionEnd);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }

      observeVisibility() {
        if (!('IntersectionObserver' in window)) return;
        this.intersectionObserver = new IntersectionObserver(([entry]) => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) this.start();
          else this.stop(false);
        }, { rootMargin: '150px 0px' });
        this.intersectionObserver.observe(this);
      }

      observeResize() {
        if (!('ResizeObserver' in window)) return;
        this.resizeObserver = new ResizeObserver(() => {
          cancelAnimationFrame(this.resizeRaf);
          this.resizeRaf = requestAnimationFrame(() => this.refresh());
        });
        // Only observe the viewport; observing the cloned inner list caused a resize loop.
        this.resizeObserver.observe(this);
      }

      refresh() {
        this.stop();
        this.removeClones();
        this.scrollLeft = 0;
        if (this.enabledForCurrentMode && this.isVisible) this.start();
      }

      createClones() {
        if (!this.inner || this.loopWidth) return;
        const items = Array.from(this.inner.children).filter((item) => !item.hasAttribute('data-auto-scroll-clone'));
        if (!items.length) return;

        const columnGap = parseFloat(getComputedStyle(this.inner).columnGap) || 0;
        // Duplicate the complete set so CSS can translate exactly one set width
        // and loop seamlessly without continuously updating scrollLeft in JS.
        const itemsToClone = items;

        const clones = itemsToClone.map((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.autoScrollClone = 'true';
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('id');
          clone.removeAttribute('role');
          clone.removeAttribute('tabindex');
          clone.removeAttribute('data-card-action');
          clone.removeAttribute('data-card-action-value');
          clone.removeAttribute('data-shopify-editor-block');
          clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => node.setAttribute('tabindex', '-1'));
          return clone;
        });
        this.inner.append(...clones);
        this.loopWidth = clones[0].offsetLeft - items[0].offsetLeft;
      }

      removeClones() {
        this.inner?.querySelectorAll('[data-auto-scroll-clone]').forEach((item) => item.remove());
        this.loopWidth = 0;
      }

      canRun() {
        return this.enabledForCurrentMode && this.isVisible && !document.hidden && this.scrollWidth > this.clientWidth;
      }

      pauseForInteraction() {
        this.isInteracting = true;
        this.inner?.classList.add('is-auto-scroll-paused');
        clearTimeout(this.resumeTimer);
      }

      resumeAfterInteraction() {
        clearTimeout(this.resumeTimer);
        this.resumeTimer = window.setTimeout(() => {
          this.isInteracting = false;
          this.inner?.classList.remove('is-auto-scroll-paused');
        }, 1200);
      }

      handleVisibilityChange() {
        if (document.hidden) this.stop(false);
        else this.start();
      }

      start() {
        if (this.autoScrollRaf || !this.enabledForCurrentMode || !this.isVisible || document.hidden) return;
        this.createClones();
        if (!this.loopWidth || this.scrollWidth <= this.clientWidth) return;
        this.inner.style.setProperty('--auto-scroll-loop-width', `${this.loopWidth}px`);
        this.inner.style.setProperty('--auto-scroll-duration', `${this.loopWidth / this.speed}ms`);
        this.inner.classList.add('is-auto-scroll-running');
      }

      stop(clearInteraction = true) {
        if (this.autoScrollRaf) cancelAnimationFrame(this.autoScrollRaf);
        this.autoScrollRaf = null;
        this.lastTimestamp = 0;
        this.inner?.classList.remove('is-auto-scroll-running', 'is-auto-scroll-paused');
        if (clearInteraction) {
          clearTimeout(this.resumeTimer);
          this.resumeTimer = null;
          this.isInteracting = false;
        }
      }
    }
  );
}
